import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, subDays, subMonths, startOfDay, endOfDay } from "date-fns";

interface Discrepancy {
  id: string;
  payment_method: string;
  expected_amount: number;
  counted_amount: number;
  difference_amount: number;
  explanation: string;
  status: string;
  counting_date: string;
  created_at: string;
}

export const useDiscrepancyManagement = () => {
  const queryClient = useQueryClient();
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<Discrepancy | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<'expected' | 'counted'>('expected');
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("all");

  const getDateRange = () => {
    const now = new Date();
    switch (selectedPeriod) {
      case "today":
        return {
          start: startOfDay(now),
          end: endOfDay(now)
        };
      case "week":
        return {
          start: startOfDay(subDays(now, 7)),
          end: endOfDay(now)
        };
      case "month":
        return {
          start: startOfDay(subMonths(now, 1)),
          end: endOfDay(now)
        };
      default:
        return null;
    }
  };

  const { data: discrepancies, refetch } = useQuery({
    queryKey: ["discrepancies", selectedPeriod],
    queryFn: async () => {
      let query = supabase
        .from("counting_discrepancies")
        .select("*")
        .order("created_at", { ascending: false });

      const dateRange = getDateRange();
      if (dateRange) {
        query = query
          .gte("counting_date", format(dateRange.start, "yyyy-MM-dd"))
          .lte("counting_date", format(dateRange.end, "yyyy-MM-dd"));
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Discrepancy[];
    },
  });

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      const discrepancy = discrepancies?.find(d => d.id === id);
      if (!discrepancy) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { error } = await supabase
        .from("counting_discrepancies")
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq("id", id);

      if (error) throw error;

      if (["approved", "deducted", "justified"].includes(newStatus)) {
        const finalAmount = newStatus === "justified" && selectedAmount === "counted" 
          ? discrepancy.counted_amount 
          : discrepancy.expected_amount;

        const { error: validationError } = await supabase
          .from("validated_amounts")
          .upsert({
            counting_date: discrepancy.counting_date,
            payment_method: discrepancy.payment_method,
            amount: finalAmount,
            status: newStatus,
            owner_id: user.id
          });

        if (validationError) throw validationError;
      }

      toast.success("Statut mis à jour avec succès");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["validatedAmounts"] });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  return {
    discrepancies,
    selectedDiscrepancy,
    setSelectedDiscrepancy,
    isDialogOpen,
    setIsDialogOpen,
    selectedAmount,
    setSelectedAmount,
    isExpanded,
    setIsExpanded,
    selectedPeriod,
    setSelectedPeriod,
    handleStatusUpdate,
    refetch
  };
};